/**
 * VulForm Shared Configuration
 * Centralized configuration that can be customized at build time and runtime
 */

// Environment-based configuration
const getEnvConfig = () => {
  const env = typeof process !== 'undefined' ? process.env : {};

  return {
    baseUrl:
      env.VULFORM_BASE_URL ||
      env.NEXT_PUBLIC_VULFORM_BASE_URL ||
      env.REACT_APP_VULFORM_BASE_URL ||
      env.VITE_VULFORM_BASE_URL,
    cdnUrl:
      env.VULFORM_CDN_URL ||
      env.NEXT_PUBLIC_VULFORM_CDN_URL ||
      env.REACT_APP_VULFORM_CDN_URL ||
      env.VITE_VULFORM_CDN_URL,
    version: env.VULFORM_VERSION || '1.0.0',
    environment: env.NODE_ENV || 'production',
  };
};

// Build-time configuration (can be replaced by bundler)
export const VULFORM_CONFIG = {
  // API Configuration - Default to SaaS but allow overrides
  DEFAULT_BASE_URL: 'https://api.vulform.dev',
  DEFAULT_CDN_URL: 'https://cdn.vulform.dev',
  DEFAULT_TIMEOUT: 10000,
  DEFAULT_RETRIES: 3,

  // Self-hosted/Development fallbacks
  SELF_HOSTED_BASE_URL: '/api/v1', // Relative URL for same-domain deployment
  LOCAL_DEV_BASE_URL: 'http://localhost:3000/api/v1',

  // SDK Configuration
  SDK_VERSION: '1.0.0',
  SDK_NAME: 'vulform-sdk',

  // Branding
  AUTHOR: 'Dogu Yilmaz',
  ORGANIZATION: 'Dogu Yilmaz',
  HOMEPAGE: 'https://vulform.dev',
  REPOSITORY: 'https://github.com/doguyilmaz/vulform',

  // Runtime overrides
  ...getEnvConfig(),
};

// Type-safe configuration interface
export interface VulFormConfig {
  baseUrl?: string;
  cdnUrl?: string;
  timeout?: number;
  retries?: number;
  version?: string;
  environment?: 'development' | 'production' | 'test';
}

// URL Resolution Strategy
export type URLResolutionStrategy =
  | 'saas' // Use SaaS endpoints (api.vulform.dev)
  | 'self-hosted' // Use relative URLs (/api/v1)
  | 'custom' // Use provided baseUrl
  | 'auto'; // Auto-detect based on environment

// Enhanced configuration builder
export class ConfigBuilder {
  private config: Required<VulFormConfig>;
  private strategy: URLResolutionStrategy = 'auto';

  constructor(overrides: Partial<VulFormConfig> = {}, strategy: URLResolutionStrategy = 'auto') {
    this.strategy = strategy;
    this.config = {
      baseUrl: this.resolveBaseUrl(overrides.baseUrl),
      cdnUrl: this.resolveCdnUrl(overrides.cdnUrl),
      timeout: overrides.timeout || VULFORM_CONFIG.DEFAULT_TIMEOUT,
      retries: overrides.retries || VULFORM_CONFIG.DEFAULT_RETRIES,
      version: overrides.version || VULFORM_CONFIG.SDK_VERSION,
      environment: overrides.environment || (VULFORM_CONFIG.environment as any) || 'production',
    };
  }

  private resolveBaseUrl(providedUrl?: string): string {
    // 1. Use explicitly provided URL
    if (providedUrl) {
      return providedUrl;
    }

    // 2. Use environment variable if set
    const envUrl = VULFORM_CONFIG.baseUrl;
    if (envUrl) {
      return envUrl;
    }

    // 3. Strategy-based resolution
    switch (this.strategy) {
      case 'saas':
        return VULFORM_CONFIG.DEFAULT_BASE_URL;

      case 'self-hosted':
        return VULFORM_CONFIG.SELF_HOSTED_BASE_URL;

      case 'custom':
        return VULFORM_CONFIG.DEFAULT_BASE_URL; // Fallback to SaaS

      case 'auto':
      default:
        // Auto-detect based on environment
        if (this.isLocalDevelopment()) {
          return VULFORM_CONFIG.LOCAL_DEV_BASE_URL;
        }

        // Check if we're in a browser and can detect same-origin
        if (typeof window !== 'undefined') {
          const currentOrigin = window.location.origin;

          // If current domain has /api/v1 endpoint, use relative URL
          // This helps with self-hosted deployments
          if (this.strategy === 'auto') {
            return VULFORM_CONFIG.SELF_HOSTED_BASE_URL;
          }
        }

        // Default to SaaS
        return VULFORM_CONFIG.DEFAULT_BASE_URL;
    }
  }

  private resolveCdnUrl(providedUrl?: string): string {
    if (providedUrl) return providedUrl;

    const envUrl = VULFORM_CONFIG.cdnUrl;
    if (envUrl) return envUrl;

    return VULFORM_CONFIG.DEFAULT_CDN_URL;
  }

  private isLocalDevelopment(): boolean {
    return (
      this.config.environment === 'development' ||
      (typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' ||
          window.location.hostname === '127.0.0.1' ||
          window.location.hostname.endsWith('.local')))
    );
  }

  setBaseUrl(url: string): ConfigBuilder {
    this.config.baseUrl = url;
    return this;
  }

  setCdnUrl(url: string): ConfigBuilder {
    this.config.cdnUrl = url;
    return this;
  }

  setEnvironment(env: 'development' | 'production' | 'test'): ConfigBuilder {
    this.config.environment = env;
    return this;
  }

  setStrategy(strategy: URLResolutionStrategy): ConfigBuilder {
    this.strategy = strategy;
    this.config.baseUrl = this.resolveBaseUrl();
    return this;
  }

  build(): Required<VulFormConfig> {
    return { ...this.config };
  }
}

// Utility functions with strategy support
export const getDefaultConfig = (
  overrides: Partial<VulFormConfig> = {},
  strategy: URLResolutionStrategy = 'auto'
): Required<VulFormConfig> => {
  return new ConfigBuilder(overrides, strategy).build();
};

// Smart URL resolution for different deployment scenarios
export const resolveApiUrl = (
  providedUrl?: string,
  strategy: URLResolutionStrategy = 'auto'
): string => {
  return new ConfigBuilder({ baseUrl: providedUrl }, strategy).build().baseUrl;
};

// Deployment scenario helpers
export const createSaaSConfig = (
  overrides: Partial<VulFormConfig> = {}
): Required<VulFormConfig> => {
  return getDefaultConfig(overrides, 'saas');
};

export const createSelfHostedConfig = (
  overrides: Partial<VulFormConfig> = {}
): Required<VulFormConfig> => {
  return getDefaultConfig(overrides, 'self-hosted');
};

export const createCustomConfig = (
  baseUrl: string,
  overrides: Partial<VulFormConfig> = {}
): Required<VulFormConfig> => {
  return getDefaultConfig({ ...overrides, baseUrl }, 'custom');
};

// Environment detection utilities
export const isDevelopment = (): boolean => {
  return VULFORM_CONFIG.environment === 'development';
};

export const isProduction = (): boolean => {
  return VULFORM_CONFIG.environment === 'production';
};

export const isSelfHosted = (): boolean => {
  const config = getDefaultConfig();
  return (
    config.baseUrl.startsWith('/') ||
    config.baseUrl.includes(typeof window !== 'undefined' ? window.location.hostname : 'localhost')
  );
};

// URL validation helpers
export const isValidVulFormUrl = (url: string): boolean => {
  try {
    if (url.startsWith('/')) return true; // Relative URLs are valid

    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

export const normalizeApiUrl = (url: string): string => {
  if (!url) return VULFORM_CONFIG.DEFAULT_BASE_URL;

  // Remove trailing slash
  url = url.replace(/\/$/, '');

  // Ensure relative URLs start with /
  if (!url.startsWith('http') && !url.startsWith('/')) {
    url = '/' + url;
  }

  return url;
};
